import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error';

interface ToastProps {
  message: string;
  type: ToastType;
  isVisible: boolean;
  onClose: () => void;
  autoHideDuration?: number;
}

const EXIT_MS = 400;

/**
 * The app's one transient message.
 *
 * It stays a controlled component — `isVisible` + `onClose` owned by the
 * caller — rather than an imperative `toast()` call, because a dozen pages
 * already hold that flag and turning it inside out would mean touching all
 * of them for no gain.
 */
export const Toast = ({ message, type, isVisible, onClose, autoHideDuration = 4000 }: ToastProps) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsClosing(true);
        setTimeout(() => {
          onClose();
          setIsClosing(false);
        }, EXIT_MS); // Match the exit animation duration
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose, autoHideDuration]);

  if (!isVisible) return null;

  const Icon = type === 'success' ? CheckCircle2 : XCircle;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-6 z-[10001] flex justify-center">
      <div
        className={cn(
          'pointer-events-auto flex max-w-[90%] items-center gap-3 rounded-xl border bg-card px-6 py-3 shadow-sm',
          isClosing
            ? 'animate-out fade-out slide-out-to-top-2 duration-400'
            : 'animate-in fade-in slide-in-from-top-4 duration-400',
        )}
      >
        <Icon
          className={cn('size-5 shrink-0', type === 'success' ? 'text-success' : 'text-destructive')}
        />
        <p className="text-sm font-medium">{message}</p>
      </div>
    </div>
  );
};
